import * as React from 'react';
import { AlertTriangle, Fuel, Pointer } from 'lucide-react';
import {
  fuelArcRecipe,
  fuelBarRecipe,
  fuelBarsRecipe,
  fuelDialRecipe,
  fuelDialTextRecipe,
  fuelHubDotRecipe,
  fuelHubRecipe,
  fuelNeedleLineRecipe,
  fuelNeedleRecipe,
  fuelScaleMarkRecipe,
  fuelScaleRecipe,
  fuelTickRecipe,
} from '../recipes/fuel-gauge.recipe';
import { Icon } from './Icon';
import { Inline, Stack } from './Layout';
import { Text } from './Text';

export interface FuelGaugeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * The readings, emptiest first: `['Empty', 'Quarter', 'Half', 'Three
   * quarters', 'Full']`. The first is the E stop and each of the rest is a
   * bar, so five readings make four bars and nine make eighths.
   */
  levels: readonly string[];
  /** The reading picked, one of `levels`. Unset until the driver taps. */
  value?: string;
  onChange?: (level: string) => void;
  /** What the radio group is called to a screen reader. */
  label?: string;
  /** The line under the bars. */
  hint?: React.ReactNode;
  /** The warning shown when the reading is in the bottom quarter. */
  lowHint?: React.ReactNode;
  disabled?: boolean;
}

// The dial's geometry, in the SVG's own units.
const CX = 100;
const CY = 100;
const R = 80;
const ARC = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

/** A point on the dial, `fraction` of the way from E (0) to F (1). */
const onDial = (fraction: number, radius: number) => {
  const radians = ((180 - fraction * 180) * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(radians),
    y: CY - radius * Math.sin(radians),
  };
};

/** Reserve is the bottom quarter, the red end of a real dial. */
const RESERVE = 0.25;

/** The mark printed under a bar: the quarters, and F at the end. */
const scaleMark = (fraction: number) => {
  if (fraction === 1) return 'F';
  const quarters = fraction * 4;
  if (Math.abs(quarters - Math.round(quarters)) > 1e-9) return '';
  return ['', '1/4', '1/2', '3/4'][Math.round(quarters)] ?? '';
};

/**
 * A fuel dial and a row of bars to set it.
 *
 *   <FuelGauge levels={['Empty', 'Quarter', 'Half', 'Three quarters', 'Full']}
 *              value={fuel} onChange={setFuel} />
 *
 * The bars are a radio group: arrow keys move the reading, Home and End jump
 * to empty and full. Nothing is picked until the driver picks it, so a
 * skipped reading never passes for "Empty".
 */
export const FuelGauge = React.forwardRef<HTMLDivElement, FuelGaugeProps>(
  function FuelGauge(
    {
      levels,
      value,
      onChange,
      label = 'Fuel level',
      hint = 'Tap a bar to match the fuel needle on the dash',
      lowHint = 'Low fuel',
      disabled = false,
      className,
      ...props
    },
    ref
  ) {
    const steps = Math.max(1, levels.length - 1);
    const index = value === undefined ? -1 : levels.indexOf(value);
    const set = index >= 0;
    const fraction = set ? index / steps : 0;
    const low = set && fraction <= RESERVE;
    const bars = React.useRef<(HTMLButtonElement | null)[]>([]);
    const hintId = React.useId();

    const pick = (next: number) => {
      const level = levels[next];
      if (level === undefined || disabled) return;
      onChange?.(level);
      bars.current[next]?.focus();
    };

    const onKeyDown = (event: React.KeyboardEvent, at: number) => {
      const next: Record<string, number> = {
        ArrowRight: Math.min(steps, at + 1),
        ArrowUp: Math.min(steps, at + 1),
        ArrowLeft: Math.max(0, at - 1),
        ArrowDown: Math.max(0, at - 1),
        Home: 0,
        End: steps,
      };
      if (next[event.key] === undefined) return;
      event.preventDefault();
      pick(next[event.key]);
    };

    const reserveEnd = onDial(RESERVE, R);

    return (
      <Stack ref={ref} gap={4} className={className} {...props}>
        <Stack gap={1} align="center">
          <svg
            viewBox="0 0 200 112"
            className={fuelDialRecipe()}
            aria-hidden="true"
          >
            <path
              d={ARC}
              strokeWidth={10}
              strokeLinecap="round"
              className={fuelArcRecipe({ part: 'track' })}
            />
            <path
              d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${reserveEnd.x} ${reserveEnd.y}`}
              strokeWidth={10}
              strokeLinecap="round"
              className={fuelArcRecipe({ part: 'reserve' })}
            />
            {set && index > 0 && (
              <path
                d={ARC}
                strokeWidth={10}
                strokeLinecap="round"
                pathLength={steps}
                strokeDasharray={`${index} ${steps}`}
                className={fuelArcRecipe({ part: 'fill', low })}
              />
            )}
            {levels.map((level, at) => {
              const f = at / steps;
              const major = scaleMark(f) !== '' || at === 0;
              const outer = onDial(f, R - 9);
              const inner = onDial(f, major ? R - 19 : R - 14);
              return (
                <line
                  key={level}
                  x1={outer.x}
                  y1={outer.y}
                  x2={inner.x}
                  y2={inner.y}
                  strokeWidth={major ? 2.5 : 1.5}
                  strokeLinecap="round"
                  className={fuelTickRecipe({ reserve: f < RESERVE })}
                />
              );
            })}
            <text
              x={CX - R + 10}
              y={CY + 10}
              textAnchor="middle"
              className={fuelDialTextRecipe({ tone: 'empty' })}
            >
              E
            </text>
            <text
              x={CX + R - 10}
              y={CY + 10}
              textAnchor="middle"
              className={fuelDialTextRecipe({ tone: 'full' })}
            >
              F
            </text>
            <text
              x={CX}
              y={CY - R + 34}
              textAnchor="middle"
              className={fuelDialTextRecipe({ tone: 'muted' })}
            >
              1/2
            </text>
            <g
              style={{
                transform: `rotate(${fraction * 180}deg)`,
                transformOrigin: `${CX}px ${CY}px`,
              }}
              className={fuelNeedleRecipe({ set })}
            >
              <line
                x1={CX + 10}
                y1={CY}
                x2={CX - R + 20}
                y2={CY}
                strokeWidth={3.5}
                strokeLinecap="round"
                className={fuelNeedleLineRecipe()}
              />
            </g>
            <circle cx={CX} cy={CY} r={7} className={fuelHubRecipe()} />
            <circle cx={CX} cy={CY} r={2.5} className={fuelHubDotRecipe()} />
          </svg>
          {/* One live region for the reading and its warning, so a screen
              reader hears "Quarter, Low fuel" together. */}
          <Stack gap={1} align="center" aria-live="polite">
            <Inline gap={2} align="center">
              <Icon
                icon={Fuel}
                size="sm"
                tone={low ? 'warning' : set ? 'default' : 'muted'}
              />
              <Text
                variant={value ? 'body-sm-strong' : 'body-sm'}
                tone={low ? 'warning' : value ? 'default' : 'muted'}
              >
                {/* A saved reading the dial has no stop for (the options
                    changed since) still says what it was, rather than
                    passing for "Not set" while it counts as answered. */}
                {value || 'Not set'}
              </Text>
            </Inline>
            {low && (
              <Inline gap={1} align="center">
                <Icon icon={AlertTriangle} size="xs" tone="warning" />
                <Text variant="caption-strong" tone="warning">
                  {lowHint}
                </Text>
              </Inline>
            )}
          </Stack>
        </Stack>

        <Stack gap={2}>
          <div
            role="radiogroup"
            aria-label={label}
            aria-describedby={hint ? hintId : undefined}
            className={fuelBarsRecipe()}
          >
            {levels.map((level, at) => {
              const filled = set && at > 0 && at <= index;
              const state =
                at === 0
                  ? index === 0
                    ? 'on'
                    : 'off'
                  : filled
                    ? low
                      ? 'low'
                      : 'on'
                    : 'off';
              return (
                <button
                  key={level}
                  ref={(node) => {
                    bars.current[at] = node;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={index === at}
                  aria-label={level}
                  tabIndex={at === Math.max(0, index) ? 0 : -1}
                  disabled={disabled}
                  onClick={() => pick(at)}
                  onKeyDown={(event) => onKeyDown(event, at)}
                  className={fuelBarRecipe({
                    kind: at === 0 ? 'empty' : 'level',
                    state,
                  })}
                >
                  {at === 0 ? 'E' : null}
                </button>
              );
            })}
          </div>
          <div className={fuelScaleRecipe()} aria-hidden="true">
            <span className={fuelScaleMarkRecipe({ spacer: true })} />
            {levels.slice(1).map((level, at) => (
              <Text
                key={level}
                as="span"
                variant="caption"
                tone="muted"
                numeric
                className={fuelScaleMarkRecipe()}
              >
                {scaleMark((at + 1) / steps)}
              </Text>
            ))}
          </div>
        </Stack>

        {hint && (
          <Inline gap={2} align="center" id={hintId}>
            <Icon icon={Pointer} size="xs" tone="muted" />
            <Text variant="caption" tone="muted">
              {hint}
            </Text>
          </Inline>
        )}
      </Stack>
    );
  }
);
